<template>
  <Teleport v-if="toolbarTarget" :to="toolbarTarget">
    <div class="bt-menu">
      <button
        class="bt-btn"
        :class="{ 'bt-btn--active': visible }"
        type="button"
        title="Atalho [B] para alternar os alvos da métrica escolhida no gráfico"
        @click="toggleVisible"
      >
        <span class="shortcut">[B]</span>
        Basis: <strong>{{ visible ? 'ON' : 'OFF' }}</strong>
        <span v-if="visible" class="bt-btn__hint">{{ metricTag }} +{{ formatPct(targetPct) }}</span>
      </button>

      <div class="bt-menu__panel">
        <div class="bt-field" title="Quantidade da próxima ordem, compra ou venda">
          <label class="bt-field__lbl">Lote:</label>
          <input
            v-model.number="lotQty"
            type="number"
            step="0.01"
            min="0.001"
            class="bt-field__input bt-field__input--qty"
          />
          <span class="bt-field__unit">BTC</span>
        </div>

        <div class="bt-field" title="Métrica que a próxima ordem deve melhorar">
          <label class="bt-field__lbl">Métrica:</label>
          <select v-model="metric" class="bt-field__select">
            <option value="spread">PMP Short − Long</option>
            <option value="locked">Locked Basis</option>
          </select>
        </div>

        <div class="bt-field" title="Aumento relativo da métrica escolhida">
          <label class="bt-field__lbl">Alvo:</label>
          <input
            v-model.number="targetPct"
            type="number"
            step="0.1"
            min="0.01"
            class="bt-field__input"
          />
          <span class="bt-field__unit">%</span>
        </div>

        <p class="bt-help">
          Cada linha é o preço em que esse lote, sozinho, leva
          {{ snapshot.metricLabel }}
          de <strong>{{ formatPnl(snapshot.currentValue) }}</strong>
          para <strong>{{ formatPnl(snapshot.targetValue) }}</strong>.
        </p>
        <div class="bt-levels">
          <div class="bt-levels__row bt-levels__row--buy">
            <span>Compra</span>
            <strong>{{ formatLevel(snapshot.buyPrice) }}</strong>
          </div>
          <div class="bt-levels__row bt-levels__row--sell">
            <span>Venda</span>
            <strong>{{ formatLevel(snapshot.sellPrice) }}</strong>
          </div>
        </div>
        <p v-if="notice" class="bt-help bt-help--warn">
          {{ notice }}
        </p>
      </div>
    </div>
  </Teleport>

  <div v-if="visible && ready" class="basis-targets__overlay">
    <div
      v-for="row in renderedRows"
      :key="row.side"
      class="bt-row"
      :class="row.side === 'buy' ? 'bt-row--buy' : 'bt-row--sell'"
      :style="{ top: row.y + 'px' }"
      @mouseenter="hoveredSide = row.side"
      @mouseleave="hoveredSide = null"
    >
      <div class="bt-row__line"></div>
      <div
        class="bt-row__label"
        :style="{ transform: 'translateY(calc(-50% + ' + row.labelShift + 'px))' }"
      >
        <span class="bt-row__tag">{{ metricTag }}</span>
        <span>{{ row.side === 'buy' ? 'BUY' : 'SELL' }}</span>
        <span>{{ formatQty(snapshot.lotQty) }}</span>
        <span>${{ formatUsd(row.price) }}</span>
      </div>

      <div
        v-if="hoveredSide === row.side"
        class="bt-tooltip"
        :style="{ transform: 'translateY(' + row.labelShift + 'px)' }"
      >
        <div class="bt-tooltip__title">
          {{ row.side === 'buy' ? 'Próxima compra' : 'Próxima venda' }}
        </div>
        <div class="bt-tooltip__meta">
          {{ formatQty(snapshot.lotQty) }} BTC @ ${{ formatUsd(row.price) }}
        </div>
        <div class="bt-tooltip__meta">
          {{ snapshot.metricLabel }} {{ formatPnl(snapshot.currentValue) }} → {{ formatPnl(snapshot.targetValue) }}
        </div>
        <div v-if="currentPrice > 0" class="bt-tooltip__meta">
          {{ formatDistance(row.price) }} do preço atual
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useBasisTargets } from './useBasisTargets'

const LABEL_GAP_PX = 18

// eslint-disable-next-line no-undef -- compiler macro (script setup)
const props = defineProps({
  priceToY: { type: Function, required: true },
  currentPrice: { type: Number, default: 0 },
  ready: { type: Boolean, default: false },
  toolbarTarget: { type: [Object, String], default: null },
})

const hoveredSide = ref(null)
const renderedRows = ref([])

const {
  visible,
  lotQty,
  targetPct,
  metric,
  snapshot,
  toggleVisible,
} = useBasisTargets()

const metricTag = computed(() => (snapshot.value.metric === 'locked' ? 'LB' : 'PMP'))

const notice = computed(() => {
  const current = snapshot.value
  const name = current.metricLabel
  if (!(current.lotQty > 0) || !(current.targetPct > 0)) {
    return 'Informe lote e alvo maiores que zero.'
  }
  if (current.currentValue != null && Math.abs(current.currentValue) > 1e-9) return ''
  if (current.pmpLong == null && current.pmpShort == null) {
    return `Sem livro para calcular ${name}.`
  }
  if (current.pmpLong == null || current.pmpShort == null) {
    return `${name} precisa de long e short. Com um lado zerado não há o que melhorar.`
  }
  return `${name} está em zero. Um alvo percentual não move o preço até o valor existir.`
})

function formatQty(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
}

function formatUsd(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return Math.round(n).toLocaleString('en-US')
}

function formatPnl(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  const sign = n > 0 ? '+' : n < 0 ? '-' : ''
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${sign}$${abs}`
}

function formatPct(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 }) + '%'
}

function formatLevel(price) {
  if (!(Number(price) > 0)) return '—'
  return '$' + formatUsd(price)
}

function formatDistance(price) {
  const delta = price - Number(props.currentPrice || 0)
  const sign = delta > 0 ? '+' : ''
  return `${sign}$${Math.round(delta).toLocaleString('en-US')}`
}

function shiftOverlappingLabels(rows) {
  const sorted = [...rows].sort((a, b) => a.y - b.y)
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    const minCenter = prev.y + prev.labelShift + LABEL_GAP_PX
    if (curr.y + curr.labelShift < minCenter) {
      curr.labelShift = minCenter - curr.y
    }
  }
  return rows
}

function updateOverlayCoordinates() {
  if (!visible.value || !props.ready || typeof props.priceToY !== 'function') {
    renderedRows.value = []
    return
  }

  const rows = []
  const levels = [
    { side: 'buy', price: snapshot.value.buyPrice },
    { side: 'sell', price: snapshot.value.sellPrice },
  ]
  for (const level of levels) {
    if (!(level.price > 0)) continue
    const y = props.priceToY(level.price)
    if (y == null) continue
    rows.push({ ...level, y, labelShift: 0 })
  }
  renderedRows.value = shiftOverlappingLabels(rows)
}

let rafId = null
function startSyncLoop() {
  function loop() {
    if (visible.value && props.ready) updateOverlayCoordinates()
    rafId = requestAnimationFrame(loop)
  }
  loop()
}

function stopSyncLoop() {
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

watch([snapshot, visible], updateOverlayCoordinates)

onMounted(() => {
  startSyncLoop()
})

onUnmounted(() => {
  stopSyncLoop()
})
</script>

<style scoped>
.bt-menu {
  position: relative;
}

.bt-menu__panel {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 240px;
  padding: 10px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  flex-direction: column;
  gap: 8px;
}

.bt-menu__panel::before {
  content: '';
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  height: 8px;
}

.bt-menu:hover .bt-menu__panel,
.bt-menu:focus-within .bt-menu__panel {
  display: flex;
}

.bt-field {
  display: flex;
  align-items: center;
  gap: 4px;
  background: #21262d;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 3px 8px;
}

.bt-field__lbl {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.bt-field__input {
  width: 64px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #d2a8ff;
  font-weight: 700;
  font-size: 12px;
  text-align: center;
  padding: 2px 0;
}

.bt-field__input--qty {
  width: 72px;
}

.bt-field__input:focus,
.bt-field__select:focus {
  outline: none;
  border-color: #a78bfa;
}

.bt-field__select {
  flex: 1;
  min-width: 0;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #d2a8ff;
  font-weight: 700;
  font-size: 11px;
  padding: 2px 4px;
}

.bt-field__unit {
  font-size: 11px;
  color: #8b949e;
  font-weight: 600;
}

.bt-help {
  margin: 0;
  padding: 6px 8px;
  background: rgba(167, 139, 250, 0.08);
  border: 1px solid rgba(167, 139, 250, 0.35);
  border-radius: 6px;
  color: #d2a8ff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.35;
}

.bt-help--warn {
  background: rgba(227, 179, 65, 0.08);
  border-color: rgba(227, 179, 65, 0.35);
  color: #e3b341;
}

.bt-levels {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bt-levels__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  font-weight: 700;
}

.bt-levels__row--buy {
  color: #3fb950;
}

.bt-levels__row--sell {
  color: #f85149;
}

.bt-btn {
  background: #21262d;
  color: #c9d1d9;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.bt-btn--active {
  background: rgba(167, 139, 250, 0.14);
  color: #d2a8ff;
  border-color: rgba(167, 139, 250, 0.7);
}

.bt-btn__hint {
  color: #a78bfa;
  font-weight: 700;
}

.shortcut {
  color: #8b949e;
  font-weight: 400;
  font-size: 11px;
}

.basis-targets__overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 7;
  overflow: visible;
}

.bt-row {
  position: absolute;
  left: 0;
  right: 72px;
  height: 0;
  pointer-events: none;
}

.bt-row__line {
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1.5px dotted;
}

.bt-row--buy .bt-row__line {
  border-top-color: #3fb950;
}

.bt-row--sell .bt-row__line {
  border-top-color: #f85149;
}

.bt-row__label {
  position: absolute;
  top: 0;
  left: 38%;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  pointer-events: auto;
  cursor: default;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.bt-row--buy .bt-row__label {
  color: #3fb950;
  background: rgba(13, 17, 23, 0.88);
  border: 1px solid rgba(63, 185, 80, 0.7);
}

.bt-row--sell .bt-row__label {
  color: #f85149;
  background: rgba(13, 17, 23, 0.88);
  border: 1px solid rgba(248, 81, 73, 0.7);
}

.bt-row__tag {
  color: #d2a8ff;
  font-weight: 800;
}

.bt-tooltip {
  position: absolute;
  top: 12px;
  left: 38%;
  min-width: 210px;
  padding: 8px 10px;
  background: #1e2430;
  border: 1px solid #a78bfa;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  color: #c9d1d9;
  font-size: 11px;
  pointer-events: none;
  z-index: 25;
}

.bt-tooltip__title {
  font-weight: 800;
  color: #f0f6fc;
  margin-bottom: 4px;
}

.bt-tooltip__meta {
  font-size: 11px;
  font-weight: 600;
  color: #c9d1d9;
}
</style>
